import { useEffect, useRef } from 'react';
import {
  WS_RECONNECT_DELAY_MS,
  WS_RECONNECT_MAX_DELAY_MS,
  POLL_INTERVAL_MS,
  parseCotSaResponse,
} from '@vtak/shared';
import type { TakContact, NormalizedSaEvent, ChatMessage, EchoMission, TakShape } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { apiClient, getApiToken } from '@/services/api-client';
import { useHealthStore } from '@/stores/health-store';
import { useContactsStore } from '@/stores/contacts-store';
import { useCotStore } from '@/stores/cot-store';
import { useChatStore } from '@/stores/chat-store';
import { useMissionsStore } from '@/stores/missions-store';
import { useShapesStore } from '@/stores/shapes-store';
import { usePluginDataStore } from '@/stores/plugin-data-store';
import { useWorkspaceStore } from '@/stores/workspace-store';

const MAX_POLL_INTERVAL_MS = 60_000;

/**
 * Open the WebSocket, carrying the access token in the `Sec-WebSocket-Protocol`
 * subprotocol (`bearer, <token>`) — the browser WS API cannot set headers, and a
 * query param would leak into logs. The server authenticates the upgrade and
 * echoes the `bearer` subprotocol. A fresh token is read on every (re)connect,
 * so a socket closed at token expiry reconnects with the refreshed token. The
 * active unit id (not a secret, unlike the token) is pinned as a `unit` query
 * param so the server resolves socket entitlements for the same unit
 * GET /api/config was resolved for.
 */
function openSocket(): WebSocket {
  const token = getApiToken();
  const url = wsUrlWithUnit(useWorkspaceStore.getState().selectedUnitId);
  return token ? new WebSocket(url, ['bearer', token]) : new WebSocket(url);
}

/** The WS endpoint with the active unit pinned as a query param, when selected. */
function wsUrlWithUnit(unitId: string | null): string {
  if (!unitId) return CLIENT_CONFIG.wsUrl;
  const url = new URL(CLIENT_CONFIG.wsUrl);
  url.searchParams.set('unit', unitId);
  return url.toString();
}

/** Connect WebSocket with auto-reconnect + HTTP polling fallback. Call once in App. */
export function useWebSocket(): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(WS_RECONNECT_DELAY_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectedRef = useRef(false);
  const pollIntervalRef = useRef(POLL_INTERVAL_MS);
  // Entitlements are resolved once per connection at upgrade time, so a unit
  // switch must re-establish the socket for the new unit's entitlements (and
  // its snapshot replay) to take effect.
  const selectedUnitId = useWorkspaceStore((s) => s.selectedUnitId);

  useEffect(() => {
    connect();

    return () => {
      teardownSocket();
      stopPolling();
    };
  }, [selectedUnitId]);

  /** Close the current socket without triggering the auto-reconnect path. */
  function teardownSocket(): void {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const ws = wsRef.current;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.close();
    wsRef.current = null;
    connectedRef.current = false;
    reconnectDelayRef.current = WS_RECONNECT_DELAY_MS;
  }

  function connect(): void {
    try {
      const ws = openSocket();
      wsRef.current = ws;
      ws.onopen = handleOpen;
      ws.onmessage = handleMessage;
      ws.onclose = handleClose;
      ws.onerror = handleError;
    } catch {
      schedulePoll();
    }
  }

  function handleOpen(): void {
    connectedRef.current = true;
    reconnectDelayRef.current = WS_RECONNECT_DELAY_MS;
    pollIntervalRef.current = POLL_INTERVAL_MS;
    stopPolling();
  }

  function handleMessage(event: MessageEvent): void {
    const message = parseMessage(event.data);
    if (!message) return;
    routeMessage(message);
  }

  function handleClose(): void {
    connectedRef.current = false;
    scheduleReconnect();
  }

  function handleError(): void {
    wsRef.current?.close();
  }

  function scheduleReconnect(): void {
    schedulePoll();
    const delay = reconnectDelayRef.current;
    reconnectDelayRef.current = Math.min(delay * 2, WS_RECONNECT_MAX_DELAY_MS);
    // Tracked so a unit switch can cancel it — otherwise a pending reconnect
    // would race the new connection and leave two sockets open.
    reconnectTimerRef.current = setTimeout(connect, delay);
  }

  function schedulePoll(): void {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setTimeout(pollFallback, pollIntervalRef.current);
  }

  function stopPolling(): void {
    if (!pollTimerRef.current) return;
    clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  }

  async function pollFallback(): Promise<void> {
    pollTimerRef.current = null;
    if (connectedRef.current) return;
    try {
      const [contacts, cotRaw, health, echoMissions] = await Promise.all([
        apiClient.getContacts(),
        apiClient.getCotSa(),
        apiClient.checkHealth(),
        apiClient.getEchoMissions(),
      ]);
      useContactsStore.getState().setContacts(contacts as TakContact[]);
      useCotStore.getState().setEvents(parseCotSaResponse(cotRaw));
      useHealthStore.getState().setConnected(health.takServer === 'reachable');
      useMissionsStore.getState().setMissions(echoMissions);
      pollIntervalRef.current = POLL_INTERVAL_MS;
    } catch {
      useHealthStore.getState().setConnected(false);
      pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, MAX_POLL_INTERVAL_MS);
    }
    schedulePoll();
  }
}

/** Loose message shape for plugin events outside the core WsMessage union */
interface RawWsMessage {
  type: string;
  payload: unknown;
}

/** Parse raw WS data into a typed message */
function parseMessage(data: unknown): RawWsMessage | null {
  try {
    return JSON.parse(String(data)) as RawWsMessage;
  } catch {
    return null;
  }
}

/** Route a WS message to the appropriate Zustand store */
function routeMessage(message: RawWsMessage): void {
  switch (message.type) {
    case 'contacts:update':
      useContactsStore.getState().setContacts(message.payload as TakContact[]);
      break;
    case 'cot:update':
      useCotStore.getState().setEvents(message.payload as NormalizedSaEvent[]);
      break;
    case 'health:update': {
      const health = message.payload as { takServer: string };
      useHealthStore.getState().setConnected(health.takServer === 'reachable');
      break;
    }
    case 'chat:message':
      useChatStore.getState().addMessage(message.payload as ChatMessage);
      break;
    case 'missions:update':
      useMissionsStore.getState().setMissions(message.payload as EchoMission[]);
      break;
    case 'shapes:update':
      useShapesStore.getState().setShapes(message.payload as TakShape[]);
      break;
    case 'areas:update':
      useShapesStore.getState().setAreaShapes(message.payload as TakShape[]);
      break;
    default:
      // Forward plugin-specific events to the generic plugin data store
      if (message.type.includes(':') && Array.isArray(message.payload)) {
        usePluginDataStore.getState().setData(message.type, message.payload);
      }
      break;
  }
}
